import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router";
import { Layout, Menu, Tag } from "antd";
import VivoLogo from "../../assets/icons/new_vivo_logo_white.svg?react";
import AlertIcon from "../../assets/icons/alert2.svg?react";
import AskVivoIcon from "../../assets/icons/ask_vivo2.svg?react";
import BookIcon from "../../assets/icons/book.svg?react";
import CheckboxIcon from "../../assets/icons/checkbox.svg?react";
import AssignmentTurnedInIcon from "../../assets/icons/assignment_turned_in.svg?react";
import ClinicalIcon from "../../assets/icons/clinical.svg?react";
import DashboardIcon from "../../assets/icons/dashboard.svg?react";
import DataIcon from "../../assets/icons/data.svg?react";
import EnrollmentIcon from "../../assets/icons/enrollment.svg?react";
import ExploreIcon from "../../assets/icons/explore.svg?react";
import GroupedBarplotIcon from "../../assets/icons/groupedBarplot.svg?react";
import HistoryIcon from "../../assets/icons/history.svg?react";
import HomeIcon from "../../assets/icons/home.svg?react";
import InsightsIcon from "../../assets/icons/insights.svg?react";
import LightbulbIcon from "../../assets/icons/lightbulb.svg?react";
import MapSearchIcon from "../../assets/icons/map_search.svg?react";
import SafetyIcon from "../../assets/icons/safety.svg?react";
import ShieldLockedIcon from "../../assets/icons/shield_locked.svg?react";
import SitesIcon from "../../assets/icons/sites.svg?react";
import SubjectsIcon from "../../assets/icons/subjects.svg?react";
import TableIcon from "../../assets/icons/table.svg?react";
import WorkspacesIcon from "../../assets/icons/workspaces.svg?react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useScopeParams } from "@/hooks/useScopeParams";

// ── Types ────────────────────────────────────────────────────────────────────

type NavSubItem = {
  id: string;
  label: string;
  href: string;
};

type NavItem = {
  id: string;
  label: string;
  icon: string;
  href?: string;
  isNew?: boolean;
  isAdvanced?: boolean;
  subitems?: NavSubItem[];
};

type NavGroup = {
  type: "group";
  label: string;
  items: NavItem[];
};

type NavDivider = { type: "divider" };

type NavReturn = {
  type: "return";
  id: string;
  label: string;
  icon: string;
};

type NavEntry = NavGroup | NavDivider | NavReturn;

// ── Nav data ─────────────────────────────────────────────────────────────────

const SIDEBAR_NAV: NavEntry[] = [
  {
    type: "group",
    label: "",
    items: [
      { id: "home", label: "Wiki Home", icon: "home", href: "wiki" },
      {
        id: "release",
        label: "Release Notes",
        icon: "history",
        href: "wiki/release-notes/all-release-notes",
      },
    ],
  },
  {
    type: "group",
    label: "BASICS",
    items: [
      {
        id: "what-is-vivo",
        label: "What is Vivo",
        icon: "explore",
        href: "wiki/guides/basics-what-is-vivo",
      },
      {
        id: "navigating-vivo",
        label: "Navigating Vivo",
        icon: "explore",
        href: "wiki/guides/basics-navigating-vivo",
      },
      {
        id: "screen-types",
        label: "Vivo Screen Types",
        icon: "map_search",
        href: "wiki/guides/basics-screen-types",
      },
    ],
  },
  {
    type: "group",
    label: "INQUIRY",
    items: [
      {
        id: "askvivo-overview",
        label: "Ask Vivo",
        icon: "askvivo",
        href: "wiki/guides/inquiry-askvivo",
      },
      {
        id: "insights-101",
        label: "Insights",
        icon: "insights",
        href: "wiki/guides/insights-enhanced",
      },
      {
        id: "alerts",
        label: "Alerts",
        icon: "alert",
        href: "wiki/guides/alerts-enhanced",
      },
      {
        id: "askvivo-user-guidelines",
        label: "User Guidelines",
        icon: "checkbox",
        href: "wiki/guides/inquiry-askvivo-user-guidelines",
      },
    ],
  },
  {
    type: "group",
    label: "EXPLORATION",
    items: [
      {
        id: "dashboard-studio-overview",
        label: "Dashboard Studio",
        icon: "explore",
        href: "wiki/guides/exploration-dashboard-studio-overview",
      },
      {
        id: "table-tab-essentials",
        label: "Tables",
        icon: "table",
        href: "wiki/guides/exploration-table-tab",
      },
      {
        id: "dashboards",
        label: "Dashboards",
        icon: "dashboard",
        href: "wiki/guides/exploration-dashboard-tab",
      },
      {
        id: "plots",
        label: "Plots",
        icon: "groupedBarplot",
        href: "wiki/guides/exploration-plot-builder",
        isAdvanced: true,
      },
      {
        id: "workspaces-deep",
        label: "Workspaces",
        icon: "workspaces",
        href: "wiki/guides/exploration-workspaces",
      },
    ],
  },
  {
    type: "group",
    label: "DOMAINS",
    items: [
      {
        id: "sites",
        label: "Sites",
        icon: "sites",
        subitems: [
          {
            id: "sites-overview",
            label: "Sites Overview",
            href: "wiki/guides/exploration-sites-overview",
          },
          {
            id: "protocol-deviations",
            label: "Protocol Deviations",
            href: "wiki/guides/exploration-sites-protocol-deviations",
          },
          {
            id: "reg-docs-tracker",
            label: "Reg Docs Tracker",
            href: "wiki/guides/exploration-reg-docs-tracker",
          },
          {
            id: "site-feasibility-board",
            label: "Feasibility Board",
            href: "wiki/guides/exploration-site-feasibility-board",
          },
        ],
      },
      {
        id: "enrollment",
        label: "Enrollment",
        icon: "enrollment",
        subitems: [
          {
            id: "trial-enrollment",
            label: "Trial Enrollment",
            href: "wiki/guides/exploration-trial-enrollment",
          },
        ],
      },
      {
        id: "subjects",
        label: "Subjects",
        icon: "subjects",
        subitems: [
          {
            id: "subjects-overview",
            label: "Participant Profiles",
            href: "wiki/guides/subjects-overview",
          },
          {
            id: "visits-activities",
            label: "Visits & Activities",
            href: "wiki/guides/exploration-subjects-visits-activities",
          },
        ],
      },
      {
        id: "single-subject",
        label: "Single Subject",
        icon: "subjects",
        subitems: [
          {
            id: "exploration-singlesubject-schedule",
            label: "Schedule",
            href: "wiki/guides/exploration-singlesubject-schedule",
          },
          {
            id: "exploration-singlesubject-screening",
            label: "Screening",
            href: "wiki/guides/exploration-singlesubject-screening",
          },
          {
            id: "exploration-singlesubject-timeline",
            label: "Timeline",
            href: "wiki/guides/exploration-singlesubject-timeline",
          },
          {
            id: "exploration-singlesubject-subject-ae",
            label: "Adverse Events",
            href: "wiki/guides/exploration-singlesubject-subject-ae",
          },
          {
            id: "exploration-singlesubject-ip-administration",
            label: "IP Administration",
            href: "wiki/guides/exploration-singlesubject-ip-administration",
          },
          {
            id: "exploration-singlesubject-ip-dispensation",
            label: "IP Dispensation",
            href: "wiki/guides/exploration-singlesubject-ip-dispensation",
          },
          {
            id: "exploration-singlesubject-missed-doses",
            label: "Missed Doses",
            href: "wiki/guides/exploration-singlesubject-missed-doses",
          },
          {
            id: "exploration-singlesubject-pharmacokinetics",
            label: "Pharmacokinetics",
            href: "wiki/guides/exploration-singlesubject-pharmacokinetics",
          },
          {
            id: "exploration-singlesubject-conmeds",
            label: "Concomitant Medications",
            href: "wiki/guides/exploration-singlesubject-conmeds",
          },
          {
            id: "exploration-singlesubject-conprocedures",
            label: "Concomitant Procedures",
            href: "wiki/guides/exploration-singlesubject-conprocedures",
          },
          {
            id: "exploration-singlesubject-assessments",
            label: "Assessments",
            href: "wiki/guides/exploration-singlesubject-assessments",
          },
          {
            id: "exploration-singlesubject-imaging",
            label: "Imaging",
            href: "wiki/guides/exploration-singlesubject-imaging",
          },
          {
            id: "exploration-singlesubject-labs",
            label: "Labs",
            href: "wiki/guides/exploration-singlesubject-labs",
          },
          {
            id: "exploration-singlesubject-questionnaires",
            label: "Questionnaires",
            href: "wiki/guides/exploration-singlesubject-questionnaires",
          },
          {
            id: "exploration-singlesubject-data-discrepancies",
            label: "Data Discrepancies",
            href: "wiki/guides/exploration-singlesubject-data-discrepancies",
          },
          {
            id: "exploration-singlesubject-deviations",
            label: "Protocol Deviations",
            href: "wiki/guides/exploration-singlesubject-deviations",
          },
        ],
      },
      {
        id: "safety",
        label: "Safety",
        icon: "safety",
        subitems: [
          {
            id: "adverse-events",
            label: "Adverse Events",
            href: "wiki/guides/exploration-safety-adverse-events",
          },
          {
            id: "conmeds",
            label: "Concomitant Medications",
            href: "wiki/guides/exploration-safety-concomitant-medications",
          },
          {
            id: "conprocedures",
            label: "Concomitant Procedures",
            href: "wiki/guides/exploration-safety-concomitant-procedures",
          },
          {
            id: "ip-admin",
            label: "IP Administration",
            href: "wiki/guides/exploration-safety-ip-admin",
          },
          {
            id: "ip-dispensation",
            label: "IP Dispensation",
            href: "wiki/guides/exploration-safety-ip-dispensation",
          },
          {
            id: "pharmacokinetics",
            label: "Pharmacokinetics",
            href: "wiki/guides/exploration-safety-pharmacokinetics",
          },
        ],
      },
      {
        id: "measures",
        label: "Measures",
        icon: "measures",
        subitems: [
          {
            id: "safety-measures",
            label: "Assessments",
            href: "wiki/guides/exploration-measures-assessments",
          },
          {
            id: "imaging",
            label: "Imaging",
            href: "wiki/guides/exploration-measures-imaging",
          },
          {
            id: "labs",
            label: "Labs",
            href: "wiki/guides/exploration-measures-labs",
          },
          {
            id: "efficacy-measure",
            label: "Questionnaires",
            href: "wiki/guides/exploration-measures-questionnaires",
          },
        ],
      },
    ],
  },
  {
    type: "group",
    label: "DATA",
    items: [
      {
        id: "data-config",
        label: "Configuration",
        icon: "data",
        href: "wiki/guides/data-connections",
      },
      {
        id: "data-discrepancies",
        label: "Discrepancies",
        icon: "assignment_turned_in",
        href: "wiki/guides/data-discrepancies",
      },
    ],
  },
  {
    type: "group",
    label: "ADMIN",
    items: [
      {
        id: "admin-portal",
        label: "Client Admin Portal",
        icon: "shield_locked",
        href: "wiki/guides/admin-portal",
        subitems: [
          {
            id: "admin-users-access",
            label: "Users & Access",
            href: "wiki/guides/admin-users-access",
          },
          {
            id: "admin-roles-permissions",
            label: "Roles & Permissions",
            href: "wiki/guides/admin-roles-permissions",
          },
          {
            id: "admin-activity-log",
            label: "Activity Log",
            href: "wiki/guides/admin-activity-log",
          },
        ],
      },
    ],
  },
  { type: "divider" },
  { type: "return", id: "return", label: "Return to Vivo", icon: "arrow-left" },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const prettifySlug = (slug: string) =>
  slug.replace(/-/g, " ").replace(/(^|\s)\S/g, (t) => t.toUpperCase());

const getLastPartOfPath = (path: string) => {
  const parts = path.split("/");
  return parts[parts.length - 1];
};

const getParentPath = (path: string) => {
  const parts = path.split("/");
  return parts.length > 1 ? parts[parts.length - 2] : null;
};

// ── Module-level icon map (built once, never rebuilt) ─────────────────────────

const ICON_STYLE = { fontSize: "20px" };

const ICON_MAP: Record<string, JSX.Element> = {
  alert: <AlertIcon style={ICON_STYLE} />,
  askvivo: <AskVivoIcon style={ICON_STYLE} />,
  book: <BookIcon style={ICON_STYLE} />,
  checkbox: <CheckboxIcon style={ICON_STYLE} />,
  assignment_turned_in: <AssignmentTurnedInIcon style={ICON_STYLE} />,
  dashboard: <DashboardIcon style={ICON_STYLE} />,
  data: <DataIcon style={ICON_STYLE} />,
  enrollment: <EnrollmentIcon style={ICON_STYLE} />,
  explore: <ExploreIcon style={ICON_STYLE} />,
  groupedBarplot: <GroupedBarplotIcon style={ICON_STYLE} />,
  history: <HistoryIcon style={ICON_STYLE} />,
  home: <HomeIcon style={ICON_STYLE} />,
  insights: <InsightsIcon style={ICON_STYLE} />,
  lightbulb: <LightbulbIcon style={ICON_STYLE} />,
  map_search: <MapSearchIcon style={ICON_STYLE} />,
  measures: <ClinicalIcon style={ICON_STYLE} />,
  safety: <SafetyIcon style={ICON_STYLE} />,
  shield_locked: <ShieldLockedIcon style={ICON_STYLE} />,
  sites: <SitesIcon style={ICON_STYLE} />,
  subjects: <SubjectsIcon style={ICON_STYLE} />,
  table: <TableIcon style={ICON_STYLE} />,
  workspaces: <WorkspacesIcon style={ICON_STYLE} />,
};

const getIcon = (iconName: string): JSX.Element | undefined =>
  ICON_MAP[iconName];

// ── Pre-built selection lookup (O(1) instead of O(n³) per render) ─────────────
// Maps every nav item id → its parent item id (or null for top-level items).

const NAV_ITEM_PARENT: Record<string, string | null> = {};

for (const entry of SIDEBAR_NAV) {
  if (entry.type !== "group") continue;
  for (const item of entry.items) {
    NAV_ITEM_PARENT[item.id] = null;
    if (item.subitems) {
      for (const sub of item.subitems) {
        NAV_ITEM_PARENT[sub.id] = item.id;
      }
    }
  }
}

// ── Search index ──────────────────────────────────────────────────────────────

type SearchEntry = {
  id: string;
  label: string;
  href: string;
  group: string;
  parent?: string;
};

function buildSearchIndex(filteredNav: NavEntry[]): SearchEntry[] {
  return filteredNav.flatMap((entry) => {
    if (entry.type !== "group") return [];
    return entry.items.flatMap((item) => {
      const entries: SearchEntry[] = [];
      if (item.href) {
        entries.push({
          id: item.id,
          label: item.label,
          href: item.href,
          group: entry.label,
        });
      }
      if (item.subitems) {
        for (const sub of item.subitems) {
          entries.push({
            id: sub.id,
            label: sub.label,
            href: sub.href,
            group: entry.label,
            parent: item.label,
          });
        }
      }
      return entries;
    });
  });
}

// ── NavBadges — eliminates 4× duplication of NEW / ADVANCED tag JSX ───────────

function NavBadges({
  isNew,
  isAdvanced,
}: {
  isNew?: boolean;
  isAdvanced?: boolean;
}) {
  return (
    <>
      {isNew && (
        <Tag className="text-white rounded-full bg-[#00A3B4] leading-3.5 p-[0px_6px] font-semibold text-[10px]">
          NEW
        </Tag>
      )}
      {isAdvanced && (
        <Tag className="text-white rounded-full bg-[#DF7934] leading-3.5 p-[0px_6px] font-semibold text-[10px]">
          ADVANCED
        </Tag>
      )}
    </>
  );
}

// ── buildMenuItems — pure function, memoizable ────────────────────────────────

function buildMenuItems(
  buildUrl: (path?: string) => string,
  filteredNav: NavEntry[],
) {
  return filteredNav
    .map((entry, index) => {
      if (entry.type === "divider") {
        return { type: "divider" as const };
      }

      if (entry.type === "return") {
        return {
          key: "return",
          label: (
            <Link
              to="/dashboard"
              className="flex items-center gap-2 text-white"
            >
              <span className="text-white">Return to Vivo</span>
            </Link>
          ),
          icon: <ArrowLeftOutlined className="text-[18px] text-white" />,
        };
      }

      return {
        key: `group-${entry.label}-${index}`,
        type: "group" as const,
        label: (
          <div className="wiki-menu-title text-xs text-[#ffffff80]">
            {entry.label}
          </div>
        ),
        children: entry.items.map((item) => {
          const labelContent = (
            <>
              {item.label}
              <NavBadges isNew={item.isNew} isAdvanced={item.isAdvanced} />
            </>
          );

          const label = item.href ? (
            <Link
              to={buildUrl(item.href)}
              className="flex items-center gap-2 text-white"
            >
              {labelContent}
            </Link>
          ) : (
            <span className="flex items-center gap-2 text-white">
              {labelContent}
            </span>
          );

          const baseItem = {
            key: item.id,
            label,
            icon: getIcon(item.icon),
          };

          if (item.subitems && item.subitems.length > 0) {
            return {
              ...baseItem,
              children: item.subitems.map((sub) => ({
                key: sub.id,
                label: (
                  <Link to={buildUrl(sub.href)} className="text-white">
                    {sub.label}
                  </Link>
                ),
              })),
            };
          }

          return baseItem;
        }),
      };
    })
    .filter(Boolean);
}

// ── Top bar with live search ──────────────────────────────────────────────────

function WikiTopBar({
  crumbs,
  buildUrl,
  searchIndex,
}: {
  crumbs: { label: string; href?: string }[];
  buildUrl: (path?: string) => string;
  searchIndex: SearchEntry[];
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const q = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (q.length === 0) return [];
    return searchIndex
      .filter(
        (e) =>
          e.label.toLowerCase().includes(q) ||
          e.group.toLowerCase().includes(q) ||
          (e.parent && e.parent.toLowerCase().includes(q)),
      )
      .slice(0, 10);
  }, [q, searchIndex]);

  const grouped = useMemo(
    () =>
      results.reduce<Record<string, { entry: SearchEntry; idx: number }[]>>(
        (acc, entry, idx) => {
          if (!acc[entry.group]) acc[entry.group] = [];
          acc[entry.group].push({ entry, idx });
          return acc;
        },
        {},
      ),
    [results],
  );

  const isOpen = query.trim().length > 0;

  const goTo = useCallback(
    (href: string) => {
      setQuery("");
      setSelectedIdx(0);
      navigate(buildUrl(href));
    },
    [navigate, buildUrl],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[selectedIdx]) {
      goTo(results[selectedIdx].href);
    } else if (e.key === "Escape") {
      setQuery("");
      setSelectedIdx(0);
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setQuery("");
        setSelectedIdx(0);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="wiki-top-bar">
      <nav className="wiki-top-bar__crumbs">
        {crumbs.map((c, i) => (
          <Fragment key={c.href ?? c.label}>
            {i > 0 && <span className="sep">/</span>}
            {c.href ? (
              <Link
                to={c.href}
                className="text-[#6b8390] hover:text-[#0c98d0] no-underline"
              >
                {c.label}
              </Link>
            ) : (
              <span className="here">{c.label}</span>
            )}
          </Fragment>
        ))}
      </nav>
      <div className="flex-1" />

      <div className="wiki-top-bar__search relative" ref={containerRef}>
        <svg viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <input
          ref={inputRef}
          placeholder="Search guides, videos, terms…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelectedIdx(0);
          }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck={false}
        />
        {isOpen && (
          <div className="absolute right-0 top-[calc(100%+8px)] z-[200] w-[300px] overflow-hidden rounded-xl border border-[#e7eaec] bg-white shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
            {results.length === 0 ? (
              <div className="px-[14px] py-4 text-center text-[13px] text-[#8fa8b2]">
                No guides found for &ldquo;{query}&rdquo;
              </div>
            ) : (
              Object.entries(grouped).map(([group, items]) => (
                <div key={group}>
                  <div className="px-[14px] pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#8fa8b2]">
                    {group}
                  </div>
                  {items.map(({ entry, idx }) => {
                    const isSelected = idx === selectedIdx;
                    return (
                      <div
                        key={entry.id}
                        onMouseEnter={() => setSelectedIdx(idx)}
                        onClick={() => goTo(entry.href)}
                        className={`flex cursor-pointer items-center gap-[9px] px-[14px] py-2 text-[14px] font-medium transition-colors duration-75 ${
                          isSelected
                            ? "bg-[#f0f9fd] text-[#0c98d0]"
                            : "bg-transparent text-[#17313a]"
                        }`}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className={`h-[14px] w-[14px] shrink-0 fill-none stroke-2 ${
                            isSelected ? "stroke-[#0c98d0]" : "stroke-[#adb8be]"
                          }`}
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                          {entry.parent && (
                            <span className="mr-1 text-[12px] text-[#8fa8b2]">
                              {entry.parent} ·
                            </span>
                          )}
                          {entry.label}
                        </span>
                        {isSelected && (
                          <span className="shrink-0 text-[11px] text-[#0c98d0]">
                            ↵
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Layout constants ──────────────────────────────────────────────────────────

const { Content, Sider } = Layout;

type LightboxState = { src: string; type: "image" | "video" } | null;

// ── WikiNavigation ────────────────────────────────────────────────────────────

const WikiNavigation = () => {
  const location = useLocation();
  const { buildUrl } = useScopeParams();
  const [lightbox, setLightbox] = useState<LightboxState>(null);

  const filteredNav = SIDEBAR_NAV;
  const searchIndex = useMemo(
    () => buildSearchIndex(filteredNav),
    [filteredNav],
  );

  // Media lightbox — delegated click handler
  useEffect(() => {
    const handleMediaClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".wiki-markdown")) return;

      if (target.tagName === "IMG") {
        const img = target as HTMLImageElement;
        if (img.classList.contains("annotated__image")) return;
        if (img.src) {
          e.preventDefault();
          setLightbox({ src: img.src, type: "image" });
        }
        return;
      }

      if (target.tagName === "VIDEO") {
        const video = target as HTMLVideoElement;
        if (video.hasAttribute("controls")) return;
        const src =
          video.src || video.querySelector("source")?.getAttribute("src") || "";
        if (src) {
          e.preventDefault();
          setLightbox({ src, type: "video" });
        }
      }
    };

    document.addEventListener("click", handleMediaClick);
    return () => document.removeEventListener("click", handleMediaClick);
  }, []);

  // Escape to close lightbox
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox]);

  // Derived state — all O(1) via pre-built lookup
  const currentKey = getLastPartOfPath(location.pathname);
  const parentPath = getParentPath(location.pathname);
  const parentMenuKey = NAV_ITEM_PARENT[currentKey] ?? null;
  const currentTitle = prettifySlug(currentKey);

  const items = useMemo(
    () => buildMenuItems(buildUrl, filteredNav),
    [buildUrl, filteredNav],
  );

  const crumbs: { label: string; href?: string }[] =
    parentPath === "guides"
      ? [{ label: "Wiki", href: buildUrl("wiki") }, { label: currentTitle }]
      : parentPath === "release-notes"
        ? [
            { label: "Wiki", href: buildUrl("wiki") },
            { label: "Release Notes" },
          ]
        : [{ label: "Wiki" }];

  const menuOpenKeys = useMemo(
    () => (parentMenuKey ? [parentMenuKey] : parentPath ? [parentPath] : []),
    [parentMenuKey, parentPath],
  );
  const [openKeys, setOpenKeys] = useState<string[]>(menuOpenKeys);

  return (
    <Layout className="h-screen">
      <Sider width={256} className="wiki-sidebar-sider fixed h-screen">
        <div className="flex flex-col h-full pt-9 pb-3 px-6">
          <div className="shrink-0 ml-1">
            <Link to={buildUrl()}>
              <VivoLogo />
            </Link>
          </div>
          <div className="flex-1 overflow-y-auto mt-5 hide-scrollbar">
            <Menu
              key={location.pathname}
              items={items}
              theme="dark"
              mode="inline"
              inlineIndent={3}
              defaultSelectedKeys={[currentKey]}
              selectedKeys={[currentKey]}
              openKeys={openKeys}
              onOpenChange={setOpenKeys}
              className="wiki-menu"
            />
          </div>
        </div>
      </Sider>

      <Layout className="relative ml-[256px] min-h-screen overflow-visible bg-[#EFF1F2]">
        <WikiTopBar
          crumbs={crumbs}
          buildUrl={buildUrl}
          searchIndex={searchIndex}
        />
        <Content className="wiki-content-scroll h-screen overflow-auto">
          <Outlet />
        </Content>
      </Layout>

      {/* Media lightbox overlay */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[2000] flex cursor-zoom-out items-center justify-center bg-[rgba(0,0,0,0.88)] p-6"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute right-5 top-4 flex h-9 w-9 items-center justify-center rounded-full border-0 bg-[rgba(255,255,255,0.12)] text-[18px] leading-none text-white transition-colors duration-150 hover:bg-[rgba(255,255,255,0.22)]"
          >
            ✕
          </button>

          {lightbox.type === "image" ? (
            <img
              src={lightbox.src}
              alt=""
              crossOrigin="anonymous"
              onClick={(e) => e.stopPropagation()}
              className="max-h-[92vh] max-w-[92vw] cursor-default rounded-[10px] object-contain shadow-[0_12px_64px_rgba(0,0,0,0.6)]"
            />
          ) : (
            <video
              src={lightbox.src}
              autoPlay
              loop
              muted
              playsInline
              controls
              crossOrigin="anonymous"
              onClick={(e) => e.stopPropagation()}
              className="max-h-[92vh] max-w-[92vw] cursor-default rounded-[10px] bg-black shadow-[0_12px_64px_rgba(0,0,0,0.6)]"
            />
          )}
        </div>
      )}
    </Layout>
  );
};

export default WikiNavigation;
